/**
 * Cycle 6 — page-view beacon.
 *
 * Fires a fire-and-forget `POST /api/pageview` when a route changes.
 * Best-effort only:
 *   • never blocks navigation
 *   • never throws
 *   • omitted if the beacon endpoint fails
 *   • uses `navigator.sendBeacon` when available so the payload is
 *     delivered even on tab close
 *
 * Privacy contract:
 *   • Requires explicit **analytics consent** (`prefs.analytics === true`)
 *     from the existing `ConsentContext` — the SAME consent gate that
 *     controls gtag / Google Analytics.  When consent is missing or set
 *     to false (including the initial pre-banner state and GPC-triggered
 *     auto-reject), NO beacon is sent.  We do NOT create a parallel
 *     consent system.
 *   • The consent record is read from `localStorage["djspy_consent_v1"]`
 *     (the same key `ConsentContext` writes) so the beacon respects
 *     revocation without a page reload.
 *
 * Session identity is a random UUID stored in `sessionStorage` under a
 * distinct key (`djs_sid`).  Unlike the B4-E `djs_did` device ID, this
 * one is per-session and only exists after analytics consent is granted;
 * the server hashes it with the pepper before storing.
 */
const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const SID_KEY = "djs_sid";
const CONSENT_KEY = "djspy_consent_v1";
const PATH_ALLOWLIST = [
  "/", "/music", "/music/free", "/music/premium",
  "/ar/music", "/ar/music/free", "/ar/music/premium",
];

/**
 * Read the site-wide analytics consent flag straight from the same
 * localStorage key `ConsentContext` writes.  Returns `true` only when
 * the visitor explicitly opted in (banner "Accept" or "Analytics only");
 * any of the following return `false`:
 *   • no consent record yet (banner not answered)
 *   • rejected via banner
 *   • auto-rejected because GPC (Sec-GPC / navigator.globalPrivacyControl)
 *   • stale policy_version
 */
export function hasAnalyticsConsent() {
  try {
    if (typeof localStorage === "undefined") return false;
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const p = JSON.parse(raw);
    return p && p.analytics === true;
  } catch { return false; }
}

function _sessionId() {
  try {
    let sid = sessionStorage.getItem(SID_KEY);
    if (sid && /^[0-9a-f-]{36}$/i.test(sid)) return sid;
    sid = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : "sid-" + Math.random().toString(36).slice(2) + Date.now();
    sessionStorage.setItem(SID_KEY, sid);
    return sid;
  } catch { return null; }
}

function _canonPath(pathname) {
  const p = String(pathname || "").split("?")[0].split("#")[0];
  if (PATH_ALLOWLIST.includes(p)) return p;
  // Track pages: `/track/:slug` — bucket by pattern so we don't blow up cardinality.
  if (/^\/track\//.test(p)) return "/track/*";
  if (/^\/ar\/track\//.test(p)) return "/ar/track/*";
  return null;
}

export function logPageview(pathname) {
  try {
    // Consent gate — must fire BEFORE any state read or network I/O.
    // Silently drops the event if analytics consent is missing.
    if (!hasAnalyticsConsent()) return;
    const path = _canonPath(pathname);
    if (!path) return;
    const url = new URLSearchParams(window.location.search);
    const body = {
      path,
      session_hash: _sessionId(),
      referrer: document.referrer || null,
      utm_source:   url.get("utm_source")   || null,
      utm_medium:   url.get("utm_medium")   || null,
      utm_campaign: url.get("utm_campaign") || null,
    };
    const endpoint = `${API_BASE}/pageview`;
    const payload = JSON.stringify(body);
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(endpoint, blob);
      return;
    }
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch { /* noop */ }
}
